/** Authenticated SI topups use server-defined packs and verified Stripe checkout destinations. */
import { API_BASE, getAuthHeaders } from '../../utils/supabase/client';

export interface SITopupOptions {
  enabled: boolean; currency: 'GBP'; environment?: 'test' | 'live'; packsPence: number[];
  disabledReason?: string; canManage: boolean;
}
export interface SICheckout { topupId: string; sessionId: string; url: string; environment: 'test' | 'live' }
export interface SIPaymentVerification { paid: boolean; credited: boolean; awaitingPaymentSync?: boolean }

async function failure(response: Response): Promise<Error> {
  const body: unknown = await response.json().catch(() => null);
  const message = body && typeof body === 'object' && 'error' in body && typeof body.error === 'string' ? body.error : 'QuantisAI billing is unavailable.';
  return new Error(message);
}
export async function loadSITopupOptions(workspaceId: string, signal?: AbortSignal): Promise<SITopupOptions> {
  const response = await fetch(`${API_BASE}/si/topups?workspaceId=${encodeURIComponent(workspaceId)}`,
    { headers: await getAuthHeaders(), signal });
  if (response.status === 403) return { enabled: false, currency: 'GBP', packsPence: [], canManage: false,
    disabledReason: 'Only workspace owners and administrators can add QuantisAI balance.' };
  if (!response.ok) throw await failure(response);
  const value: unknown = await response.json();
  if (!value || typeof value !== 'object') throw new Error('QuantisAI billing returned invalid options.');
  const options = value as Record<string, unknown>;
  if (typeof options.enabled !== 'boolean' || options.currency !== 'GBP' || !Array.isArray(options.packsPence)
    || options.packsPence.length > 10 || options.packsPence.some(pack => !Number.isSafeInteger(pack) || pack < 1 || pack > 100000)
    || (options.enabled && (!options.packsPence.length || !['test', 'live'].includes(String(options.environment)))))
    throw new Error('QuantisAI billing returned invalid options.');
  return { enabled: options.enabled, currency: 'GBP', packsPence: options.packsPence,
    environment: options.environment === 'test' || options.environment === 'live' ? options.environment : undefined,
    canManage: true, disabledReason: typeof options.disabledReason === 'string' ? options.disabledReason
      : options.enabled ? undefined : 'Topups are disabled until charging and measured pricing are configured.' };
}
export function validatedSICheckout(value: unknown): SICheckout {
  if (!value || typeof value !== 'object') throw new Error('QuantisAI checkout returned invalid details.');
  const checkout = value as Record<string, unknown>;
  if (typeof checkout.topupId !== 'string' || !/^[0-9a-f-]{36}$/i.test(checkout.topupId)
    || typeof checkout.sessionId !== 'string' || !/^cs_(test_|live_)?[A-Za-z0-9_]+$/.test(checkout.sessionId)
    || typeof checkout.url !== 'string' || !['test', 'live'].includes(String(checkout.environment)))
    throw new Error('QuantisAI checkout returned invalid details.');
  const url = new URL(checkout.url);
  if (url.protocol !== 'https:' || url.hostname !== 'checkout.stripe.com' || url.username || url.password || url.port)
    throw new Error('QuantisAI checkout returned an untrusted payment destination.');
  return checkout as unknown as SICheckout;
}
export async function createSITopup(workspaceId: string, amountPence: number, signal?: AbortSignal): Promise<SICheckout> {
  const response = await fetch(`${API_BASE}/si/topups`, { method: 'POST',
    headers: await getAuthHeaders({ 'Content-Type': 'application/json' }), signal,
    body: JSON.stringify({ workspaceId, amountPence }) });
  if (!response.ok) throw await failure(response);
  return validatedSICheckout(await response.json());
}
export async function verifySITopup(workspaceId: string, topupId: string, signal?: AbortSignal): Promise<SIPaymentVerification> {
  const response = await fetch(`${API_BASE}/si/topups/${encodeURIComponent(topupId)}/verify`, { method: 'POST',
    headers: await getAuthHeaders({ 'Content-Type': 'application/json' }), signal, body: JSON.stringify({ workspaceId }) });
  if (!response.ok) throw await failure(response);
  const value: unknown = await response.json();
  if (!value || typeof value !== 'object' || !('paid' in value) || typeof value.paid !== 'boolean'
    || !('credited' in value) || typeof value.credited !== 'boolean' || (value.credited && !value.paid)
    || ('awaitingPaymentSync' in value && typeof value.awaitingPaymentSync !== 'boolean')) throw new Error('QuantisAI payment verification was invalid.');
  return value as SIPaymentVerification;
}
